import React, { useState } from 'react'
import { Box, Checkbox, FormControlLabel, Menu } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { banhangKiotLike } from '@/components/layout/banhangKiotTokens'
import { ListToolbarIconButton } from './ListToolbarButtons'
import { listToolbarColumnMenuCheckboxSx } from './listToolbarStyles'

export type ListToolbarColumnMenuItem = { key: string; label: string }

export type ListToolbarColumnMenuProps = {
  items: ListToolbarColumnMenuItem[]
  hiddenKeys: Set<string>
  onToggle: (key: string) => void
  ariaLabel: string
  /** Icon trong nút (mặc định cột + chevron). */
  icon?: React.ReactNode
  menuLayout?: 'list' | 'grid'
  menuPaperSx?: SxProps<Theme>
}

export const buildListToolbarColumnTriggerIcon = (leading: React.ReactNode) => (
  <Box
    component="span"
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 0,
      color: 'primary.contrastText',
    }}
  >
    {leading}
    <ArrowDropDownIcon sx={{ fontSize: 18, display: 'block', ml: '-6px', opacity: 0.95 }} />
  </Box>
)

const defaultTriggerIcon = buildListToolbarColumnTriggerIcon(
  <ViewColumnOutlinedIcon sx={{ fontSize: 18, display: 'block' }} />,
)

export const ListToolbarColumnMenu = ({
  items,
  hiddenKeys,
  onToggle,
  ariaLabel,
  icon = defaultTriggerIcon,
  menuLayout = 'list',
  menuPaperSx,
}: ListToolbarColumnMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const paperBase: SxProps<Theme> = {
    mt: 0.5,
    maxHeight: 360,
    borderRadius: 1,
    border: `1px solid ${banhangKiotLike.kiotTableOuterBorder}`,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    ...(menuLayout === 'grid'
      ? {
          minWidth: { xs: 280, sm: 408 },
          maxWidth: 'min(100vw - 16px, 520px)',
          p: 1.5,
        }
      : {
          minWidth: 220,
        }),
  }

  const paperSx: SxProps<Theme> = menuPaperSx
    ? ([paperBase, menuPaperSx] as SxProps<Theme>)
    : paperBase

  const checkboxRows = items.map((item) => (
    <FormControlLabel
      key={item.key}
      sx={
        menuLayout === 'grid'
          ? listToolbarColumnMenuCheckboxSx
          : { ...listToolbarColumnMenuCheckboxSx, display: 'flex', px: 1.5, py: 0.35 }
      }
      control={
        <Checkbox size="small" checked={!hiddenKeys.has(item.key)} onChange={() => onToggle(item.key)} />
      }
      label={item.label}
    />
  ))

  return (
    <>
      <ListToolbarIconButton aria-label={ariaLabel} onClick={(e) => setAnchorEl(e.currentTarget)}>
        {icon}
      </ListToolbarIconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: paperSx } }}
      >
        {menuLayout === 'grid' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              columnGap: 2,
              rowGap: 0.65,
            }}
          >
            {checkboxRows}
          </Box>
        ) : (
          checkboxRows
        )}
      </Menu>
    </>
  )
}

export default ListToolbarColumnMenu
